import {forkJoin, map, Observable, of, switchMap} from 'rxjs';

import {CycleTagType, ICycle, IField, IGetTemplateByNameRequest, IGetTemplateByNameResponse, IMetaTagValue, IToTemplateRequest, TemplateFormCycleType} from '@core/api/data-access';

import {CyclesService} from './cycles.service';

export class TemplateCyclesService {
	constructor(private cyclesService: CyclesService) {}

	saveAsTemplate(cycle: ICycle, templateType: TemplateFormCycleType = TemplateFormCycleType.PrivateTemplate): Observable<object> {
		return this.cyclesService.apiService.post<IToTemplateRequest, object>(this.cyclesService.getUrl('template'), {cycleId: cycle.id, status: templateType});
	}

	/**
	 * Get template by name - Use to verify if template name is already exists
	 * @param templateName
	 * @returns {Observable<IGetTemplateByNameResponse>} - return empty object if template name is not exists
	 */
	getTemplateByName(templateName: string): Observable<IGetTemplateByNameResponse> {
		const request: IGetTemplateByNameRequest = {templateName};
		return this.cyclesService.apiService.get<IGetTemplateByNameResponse>(this.cyclesService.getUrl('template'), request);
	}

	/**
	 * Check if template name already exists,	if yes then prompt user to enter new name
	 * @param templateName
	 */
	public templateNameExistsCheck(templateName: string): Observable<string> {
		return this.getTemplateByName(templateName).pipe(
			switchMap(response => {
				if (response?.cycle?.name === templateName) {
					const promptMessage = window.translate('services.cycles.template.template-name-exist');
					const newTemplateName = prompt(promptMessage);
					if (newTemplateName) {
						return this.templateNameExistsCheck(newTemplateName);
					}
				}
				return of(templateName);
			}),
		);
	}

	/**
	 * @description Clone template
	 * @param {string} templateId
	 * @param {string} document
	 * @param {IField[]} fields {optional}
	 * @param {CycleTagType[]} tags {optional}
	 * @param {IMetaTagValue[]} metaTags {optional}
	 * @returns {Observable<ICycle>}
	 * @memberof TemplateCyclesService
	 */
	public cloneTemplate(templateId: string, document?: string, fields?: IField[], tags?: CycleTagType[], metaTags?: IMetaTagValue[]): Observable<ICycle> {
		const fields$: Observable<IField[]> = fields ? of([]) : of(undefined);
		const cycle$: Observable<ICycle> = this.cyclesService.Cycle.get(templateId).pipe(
			map(response => {
				const cycle = response.cycle;
				if (tags) cycle.tags = tags;
				if (metaTags) cycle.metaTags = metaTags;
				return cycle;
			}),
		);
		return forkJoin([fields$, cycle$]).pipe(switchMap(([fields, cycle]) => this.cyclesService.Cycle.duplicate(cycle.id, cycle.name, document, fields, cycle.metaTags, cycle.tags)));
	}
}
